import React, { useCallback, useMemo, useState } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useSelector } from 'react-redux';
import { generatePath } from 'react-router';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';

import { Box, Grid } from '@mui/material';
import { Parameters } from '@provider-portal/common/api/gen/DeliveryProviderPortalAuthApi';
import { ErrorWrapper } from '@provider-portal/common/components/ErrorWrapper/ErrorWrapper';
import { ProviderPermissions } from '@provider-portal/common/services/types/account';
import { isEqual } from 'lodash';
import queryString from 'query-string';
import MenuEditor from 'widgets/MenuEditor/MenuEditor';
import { NavBar } from 'widgets/NavBar/NavBar';
import OpeningTimes from 'widgets/OpeningTimes/OpeningTimes';

import { Facade } from '@bolteu/lattice';

import { selectSelectedProviderPermissions } from 'rdx/account/selectors';
import { PageChangeTracker } from 'services/analytics/PageChangeTracker';

import { AccessControl } from 'components/AccessControl/AccessControl';
import { GenericErrorPage } from 'components/GenericErrorPage/GenericErrorPage';
import { TopBar } from 'components/TopBar/TopBar';
import { buildMenuFromConfigAndModules, MenuContext } from 'root/navigation/navStructureUtils';
import { AccountUnlock } from 'screens/AccountUnlock/AccountUnlock';
import { ForgotPassword } from 'screens/ForgotPassword/ForgotPassword';
import { Login } from 'screens/Login/Login';
import { MainContent } from 'screens/MainContent/MainContent';

export enum AppRoutes {
  AUTH = '/login',
  AUTH_WITH_TOKEN = '/token-login/:token',
  LOGOUT = '/logout',
  HOME = '/',
  MENU_EDITOR = '/management/menuEditor',
  OPENING_TIMES = '/management/openingTimes',
  FORGOT_PASSWORD = '/forgotPassword',
  UNLOCK_ACCOUNT = '/unlockAccount',
  SUPPORT_REDIRECT = '/supportCaseRedirect',
}

export const BackwardsCompatibleRedirects = [
  // /menu was renamed to /management
  { from: '/menu/menuEditor', to: AppRoutes.MENU_EDITOR },
  { from: '/menu/openingTimes', to: AppRoutes.OPENING_TIMES },
  // Redir to the lattice module's path
  { from: '/menu/providerDetails', to: '/management/provider-details' },
];

export const routeWidgets: Partial<Record<AppRoutes, React.FC>> = {
  [AppRoutes.MENU_EDITOR]: MenuEditor,
  [AppRoutes.OPENING_TIMES]: OpeningTimes,
};

export const routeWidgetEntries = Object.entries(routeWidgets);

export const defaultRoute = '/dashboard/home';
export const onboardingRoute = '/partner-onboarding/';

export const addNewMerchantVenueRoute = '/partner-onboarding/new-venue';

const deductionsDefaultRoute = '/deductions/list';

export const AuthenticationRoutes = () => {
  const renderCallback = useCallback(({ location }: RouteComponentProps) => {
    const parsedQuery = queryString.parse(location.search);
    const to = parsedQuery.to || defaultRoute;

    const query = queryString.stringify({ to });
    const redirectPath = generatePath(AppRoutes.AUTH, {});
    return <Redirect to={`${redirectPath}?${query}`} />;
  }, []);

  return (
    <Switch>
      <Route path={AppRoutes.AUTH} key={AppRoutes.AUTH}>
        <Login />
      </Route>
      <Route exact path={AppRoutes.FORGOT_PASSWORD} key={AppRoutes.FORGOT_PASSWORD}>
        <ForgotPassword />
      </Route>
      <Route exact path={AppRoutes.UNLOCK_ACCOUNT} key={AppRoutes.UNLOCK_ACCOUNT}>
        <AccountUnlock />
      </Route>
      <Route path="*" render={renderCallback} key="unauthenticated-redirect" />
    </Switch>
  );
};

interface ProtectedRoutesProps {
  moduleManifests: Facade.ModuleManifest[];
  targetingParameters: Parameters | null;
}

export const ProtectedRoutes: React.FC<ProtectedRoutesProps> = ({
  children,
  moduleManifests,
  targetingParameters,
}) => {
  const [isMobileNavOpen, setMobileNavOpen] = useState(false);
  const handleOpenMobileNav = useCallback(() => setMobileNavOpen(true), []);
  const handleCloseMobileNav = useCallback(() => setMobileNavOpen(false), []);

  const providerPermissions = useSelector(selectSelectedProviderPermissions, isEqual);

  // DPX-2882: temporary solution before proper permissions system to only show specific page(s) for specific role
  const exclusivePermissions: Partial<ProviderPermissions> = useMemo(
    () => ({
      deductionsAccess:
        targetingParameters?.delivery_provider_portal_flags?.permission_deductions_only,
    }),
    [targetingParameters],
  );

  const menuItems = useMemo(
    () =>
      buildMenuFromConfigAndModules(
        moduleManifests,
        providerPermissions,
        exclusivePermissions,
        targetingParameters,
      ),
    [moduleManifests, providerPermissions, exclusivePermissions, targetingParameters],
  );

  const renderCallback = useCallback(
    ({ location }: RouteComponentProps) => {
      if (exclusivePermissions.deductionsAccess) {
        return <Redirect to={deductionsDefaultRoute} />;
      }

      const query = queryString.parse(location.search);
      return <Redirect to={String(query.to || defaultRoute)} />;
    },
    [exclusivePermissions],
  );

  return (
    <Route path={AppRoutes.HOME} key={AppRoutes.HOME}>
      <MenuContext.Provider value={{ menu: menuItems }}>
        <AccessControl>
          <PageChangeTracker />
          <TopBar onMobileNavOpen={handleOpenMobileNav} />
          <NavBar onMobileClose={handleCloseMobileNav} isMobileView={isMobileNavOpen} />
          <ErrorWrapper
            redirectElement={
              <main className="mainContent">
                <PerfectScrollbar>
                  <Grid sx={{ ml: { xl: '250px' }, px: 4, py: 3 }}>
                    <Grid item xs={12}>
                      <Box mt={5}>
                        <GenericErrorPage pageHeight="80vh" />
                      </Box>
                    </Grid>
                  </Grid>
                </PerfectScrollbar>
              </main>
            }
          >
            <MainContent>{children}</MainContent>
          </ErrorWrapper>
          <Route
            path={[AppRoutes.AUTH, AppRoutes.HOME]}
            exact
            render={renderCallback}
            key="unauthenticated-redirect"
          />

          {BackwardsCompatibleRedirects.map((redir) => (
            <Route path={redir.from} key={redir.from}>
              <Redirect to={redir.to} />
            </Route>
          ))}
        </AccessControl>
      </MenuContext.Provider>
    </Route>
  );
};
